import React, { useState, useContext, useEffect, useCallback } from 'react';
import ModalContext from '../../../../contexts/ModalContext';
import { useOutletContext } from 'react-router-dom';
import { ProductionFashionOutletType } from '..';
import { GlobalTable, HeaderLine, Td, Th, Tr } from '../../../../components/styles/table-style';
import { Actions, Select } from './style';
import arrowDown from '../../../../assets/addStockModal/excel/arrow-down.svg';
import search from '../../../../assets/productions/search-in.svg';
import pen from '../../../../assets/productions/pen.svg';
import play from '../../../../assets/production-flow/play-circle.svg';
import FashionApiService from '../../../../services/FashionApiService'; // use centralized service
import eventBus from '../../../../utils/eventBus';

// --- NOVAS INTERFACES DE DADOS DO BACKEND ---

export interface CompanyData {
  id: number;
  companyName: string;
}

// Interface para um FashionProductionOrderItem (item de ligação entre FPO e FashionOrder)
interface FashionProductionItemsData {
  id: number;
  fashionProductionOrderId: number;
  fashionOrderId: number;
  quantityForThisProduction: number;
  fashionOrder?: {
    id: number;
  };
}

interface UserProfileData {
  id: number;
  name: string; // Ex: "Cortador", "Costureira"
}

// Interface para o objeto FashionProductionOrder que virá do backend
interface FashionProductionOrderData {
  id: number;
  set: string; // Ex: "Camisa A - Cor: Branca"
  status: string;
  dressmakerId: number | null; // ID do cortador (dressmaker)
  cutterId: number | null; // ID da costureira (cutter)
  fashionProductId?: number;
  colorId?: number;

  fashionProduct?: { id: number; name: string };
  color?: { id: number; name: string };
  fashionProductionItems?: FashionProductionItemsData[];

  // Incluir as relações para dressmaker e cutter aqui
  // O backend deve estar populando estes campos!
  dressmaker?: UserData;
  cutter?: UserData;

  totalItems?: number;
}

interface UserData {
  id: number;
  name: string; // Nome direto do usuário (ex: Joana, Fizza Now)
  email?: string;
  // role?: string; // Este campo parece não ser a fonte da role no seu JSON, pode ser removido ou opcional
  profile?: UserProfileData; // O perfil do usuário com o nome da role
  profileId?: number | null; // Adicionado para facilitar a filtragem por ID do perfil
}

// --- FIM DAS NOVAS INTERFACES ---

const ProductionFashionTable: React.FC = () => {
  const { setTabIndex } = useOutletContext<ProductionFashionOutletType>();
  const { setModalActive, setModalSection, setSelectedProductionOrder } = useContext(ModalContext);

  const columns = ["OP", "Pedido", "Conjunto", "Qtd. de itens", "Status", "Cortador", "Costureira", ""];

  const [productionOrders, setProductionOrders] = useState<FashionProductionOrderData[]>([]);
  const [cuttersList, setCuttersList] = useState<UserData[]>([]);
  const [dressmakersList, setDressmakersList] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // 1. Buscar Ordens de Produção
      const productionOrdersResponse = await FashionApiService.getProductionOrders();

      // 2. Buscar Usuários (Cortadores e Costureiras) no backend já filtrados por role e tenant
      const [cuttersResp, dressmakersResp] = await Promise.all([
        FashionApiService.getUsers('Cortador'),
        FashionApiService.getUsers('Costureira')
      ]);

      const cutters: UserData[] = cuttersResp.data || [];
      const dressmakers: UserData[] = dressmakersResp.data || [];

      if (cutters.length === 0) console.warn("Nenhum cortador retornado pelo backend para este tenant.");
      if (dressmakers.length === 0) console.warn("Nenhuma costureira retornada pelo backend para este tenant.");

      setCuttersList([{ id: 0, name: "Selecione..." }, ...cutters]);
      setDressmakersList([{ id: 0, name: "Selecione..." }, ...dressmakers]);

      const mappedOrders: FashionProductionOrderData[] = productionOrdersResponse.data.map((order: FashionProductionOrderData) => {
        const totalItemsInProduction = order.fashionProductionItems?.reduce((sum, item) => sum + item.quantityForThisProduction, 0) || 0;

        return {
          ...order,
          totalItems: totalItemsInProduction,
        };
      });

      setProductionOrders(mappedOrders);

    } catch (err: any) {
      console.error('Erro ao buscar dados:', err);
      setError('Falha ao carregar ordens de produção ou usuários.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // --- NOVO useEffect para ouvir o Event Bus ---
  useEffect(() => {
    const handleRefreshTable = () => {
      fetchData(); // Chama a função que busca os dados novamente
    };

    eventBus.on('refreshProductionTable', handleRefreshTable); // Liga o listener

    // Função de limpeza: remove o listener quando o componente desmonta
    return () => {
      eventBus.off('refreshProductionTable', handleRefreshTable); // Desliga o listener
    };
  }, [fetchData]); // `fetchData` como dependência para garantir que a função mais recente seja usada

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // *** NOVO useEffect para depuração do preenchimento do Select ***
  useEffect(() => {
    if (productionOrders.length > 0 && cuttersList.length > 1 && dressmakersList.length > 1) { // >1 para ignorar "Selecione..."
      productionOrders.forEach(orderItem => {
        const assignedCutter = cuttersList.find(c => c.id === orderItem.cutterId);
        const assignedDressmaker = dressmakersList.find(d => d.id === orderItem.dressmakerId);
      });
    }
  }, [productionOrders, cuttersList, dressmakersList]);

  // Função para lidar com a mudança no select do cortador
  const handleCortadorChange = async (e: React.ChangeEvent<HTMLSelectElement>, orderId: number) => {
    const selectedUserId = Number(e.target.value);
    const selectedUserObject = cuttersList.find(c => c.id === selectedUserId);

    // Atualização otimista da UI
    setProductionOrders(prevOrders =>
      prevOrders.map(order =>
        order.id === orderId ? {
          ...order,
          cutterId: selectedUserId === 0 ? null : selectedUserId,
          cutter: selectedUserId === 0 ? undefined : selectedUserObject
        } : order
      )
    );

    try {
      await FashionApiService.updateProductionOrder(orderId, {
        cutterId: selectedUserId === 0 ? null : selectedUserId,
      });
      const newCutterName = selectedUserObject?.name || 'Nenhum';
      // feedback com alert para sucesso
      alert(`SUCESSO: Cortador da OP-${orderId} atualizado para ${newCutterName}!`);
    } catch (err) {
      console.error(`Erro ao atualizar cortador da OP ${orderId}:`, err);
      // feedback com alert para erro
      alert(`ERRO: Falha ao atualizar cortador da OP-${orderId}. Verifique o console.`);
      fetchData(); // Recarrega para sincronizar em caso de erro
    }
  };

  // Função para lidar com a mudança no select da costureira
  const handleCostureiraChange = async (e: React.ChangeEvent<HTMLSelectElement>, orderId: number) => {
    const selectedUserId = Number(e.target.value);
    const selectedUserObject = dressmakersList.find(d => d.id === selectedUserId);

    // Atualização otimista da UI
    setProductionOrders(prevOrders =>
      prevOrders.map(order =>
        order.id === orderId ? {
          ...order,
          dressmakerId: selectedUserId === 0 ? null : selectedUserId,
          dressmaker: selectedUserId === 0 ? undefined : selectedUserObject
        } : order
      )
    );

    try {
      await FashionApiService.updateProductionOrder(orderId, {
        dressmakerId: selectedUserId === 0 ? null : selectedUserId,
      });
      const newDressmakerName = selectedUserObject?.name || 'Nenhum';
      // feedback com alert para sucesso
      alert(`SUCESSO: Costureira da OP-${orderId} atualizada para ${newDressmakerName}!`);
    } catch (err) {
      console.error(`Erro ao atualizar costureira da OP ${orderId}:`, err);
      // feedback com alert para erro
      alert(`ERRO: Falha ao atualizar costureira da OP-${orderId}. Verifique o console.`);
      fetchData(); // Recarrega para sincronizar em caso de erro
    }
  };

  // MODIFIQUE A FUNÇÃO handleView
  const handleView = (order: FashionProductionOrderData) => {
    // 1. Armazena a ordem de produção clicada no contexto
    setSelectedProductionOrder(order);
    // 2. Ativa o modal
    setModalActive(true);
    // 3. Define a seção do modal (se ViewProductionFashion for a única, você pode até simplificar)
    setModalSection('viewProductionFashion');
  };

  if (isLoading) {
    return <div>Carregando ordens de produção...</div>;
  }

  if (error) {
    return <div>Erro: {error}</div>;
  }

  return (
    <GlobalTable>
      <thead>
        <HeaderLine>
          {columns.map((item, index) => (
            <Th key={index}>{item}</Th>
          ))}
        </HeaderLine>
      </thead>
      <tbody>
        {productionOrders.map((orderItem) => (
          <Tr key={orderItem.id}>
            <Td>{`OP-${orderItem.id}`}</Td>
            <Td>
              {orderItem.fashionProductionItems && orderItem.fashionProductionItems.length > 0
                ? orderItem.fashionProductionItems.map(item => `PED-${item.fashionOrderId}`).join(', ')
                : 'N/A'}
            </Td>
            <Td>{orderItem.set}</Td>
            <Td>{orderItem.totalItems}</Td>
            <Td>{orderItem.status}</Td>
            <Td>
              <div>
                <Select
                  value={orderItem.cutterId || 0} /* CORREÇÃO AQUI: O valor do Select deve ser o ID */
                  onChange={(e) => handleCortadorChange(e, orderItem.id)}
                  className="p-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {cuttersList.map((cutter) => (
                    <option key={cutter.id} value={cutter.id}>
                      {cutter.name}
                    </option>
                  ))}
                </Select>
                <img style={{ marginLeft: '-2rem', pointerEvents: 'none' }} src={arrowDown} alt="Seta para baixo" />
              </div>
            </Td>
            <Td>
              <div>
                <Select
                  value={orderItem.dressmakerId || 0} /* CORREÇÃO AQUI: O valor do Select deve ser o ID */
                  onChange={(e) => handleCostureiraChange(e, orderItem.id)}
                  className="p-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {dressmakersList.map((dressmaker) => (
                    <option key={dressmaker.id} value={dressmaker.id}>
                      {dressmaker.name}
                    </option>
                  ))}
                </Select>
                <img style={{ marginLeft: '-2rem', pointerEvents: 'none' }} src={arrowDown} alt="Seta para baixo" />
              </div>
            </Td>
            <Td>
              <Actions>
                <img onClick={() => handleView(orderItem)} src={search} alt="Visualizar ordem" />
                <img src={pen} alt="Editar ordem" />
                <img src={play} alt="Iniciar ordem" />
              </Actions>
            </Td>
          </Tr>
        ))}
      </tbody>
    </GlobalTable>
  );
};

export default ProductionFashionTable;

